import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import {useApi} from '../../lib/api';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import {Checkbox} from '../FormBuilderScreen/QuestionBuilder';
import {alphaSort} from '../../lib/sort';
import search from '../../lib/search';
import {getScaleTarget} from "../FormBuilderScreen/shared";

function AttachTemplatesModal({layout, open, templates = [], templatesLoading = false, onClose, onSaved}) {
  const api = useApi();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [initialIds, setInitialIds] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [scaleFilter, setScaleFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!layout) return;
    setFilter('');
    setScaleFilter('all');
    const initial = new Set(
      templates
        .filter(t => t.labelLayoutId === layout.id)
        .map(t => t.id)
    );
    setInitialIds(initial);
    setSelectedIds(new Set(initial));
  }, [layout, templates]);

  const loading = templatesLoading;

  const toggleTemplate = (templateId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!layout) return;

    const toAttach = [...selectedIds].filter(id => !initialIds.has(id));
    const toDetach = [...initialIds].filter(id => !selectedIds.has(id));

    if (toAttach.length === 0 && toDetach.length === 0) {
      onClose();
      return;
    }

    setSaving(true);

    const updates = [
      ...toAttach.map(id => ({id, labelLayoutId: layout.id})),
      ...toDetach.map(id => ({id, labelLayoutId: null}))
    ];

    const failures = [];
    for (const {id, labelLayoutId} of updates) {
      try {
        const fullTemplate = await api.getTemplate(id);
        await api.editTemplate({...fullTemplate, labelLayoutId});
      } catch (e) {
        failures.push(id);
      }
    }

    setSaving(false);

    // If it fails to update all templates do not refresh the templates list
    if (failures.length === updates.length) {
      NotificationManager.error('Failed to update template assignments');
      onClose();
      return;
    }

    // Show error for ones that failed to update, but still call onSaved to update the templates list
    if (failures.length > 0) {
      NotificationManager.error(`Updated ${updates.length - failures.length} of ${updates.length} templates. ${failures.length} failed.`);
    } else {
      NotificationManager.success(`Updated template assignments for ${layout.name}`);
    }
    onSaved();
  };

  // any unarchived form can use a layout, a form without a scale question just prints its labels by hand
  const eligibleTemplates = templates.filter(t => t.current === true);

  const filteredTemplates = eligibleTemplates
    .filter(t => search(t.name, filter))
    .filter(t => scaleFilter === 'all' || hasScaleQuestion(t) === (scaleFilter === 'scale'))
    .sort(alphaSort('asc', t => t.name));

  const eligibleIds = new Set(eligibleTemplates.map(t => t.id));
  const selectedEligibleCount = [...selectedIds].filter(id => eligibleIds.has(id)).length;

  const changedCount =
    [...selectedIds].filter(id => !initialIds.has(id)).length +
    [...initialIds].filter(id => !selectedIds.has(id)).length;

  return (
    <Form onSubmit={handleSave}>
      <SideModal
        open={open}
        title={layout ? `${layout.name}` : 'Attach Templates'}
        onClose={saving ? () => {} : onClose}
        options={[
          <Button key="save" type="submit" disabled={saving || loading || changedCount === 0}>
            {saving ? 'Saving...' : changedCount > 0 ? `Save (${changedCount} change${changedCount === 1 ? '' : 's'})` : 'Save'}
          </Button>,
          <Button key="cancel" tertiary onClick={onClose} disabled={saving}>Cancel</Button>
        ]}
      >
        <Content>
          {loading ? (
            <Loading />
          ) : (
            <>
              <Description>
                Select the forms that should use this print layout.
                Saving will update each form's printer label assignment.
                <br />
                <br />
                Any form can use this print layout. Only forms with a question set as a scale target print a label
                automatically when something is weighed.
              </Description>
              <Filters>
                <Input
                  id="attach-templates-filter"
                  type="text"
                  placeholder="Search forms..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <Input
                  id="attach-templates-scale-filter"
                  type="select"
                  aria-label="Filter by scale questions"
                  value={scaleFilter}
                  onChange={e => setScaleFilter(e.target.value)}>
                  <option value="all">All forms</option>
                  <option value="scale">With scale questions</option>
                  <option value="no-scale">Without scale questions</option>
                </Input>
              </Filters>
              <SelectionSummary>
                {selectedEligibleCount} of {eligibleTemplates.length} selected
              </SelectionSummary>
              <TemplateList>
                {filteredTemplates.length === 0 ? (
                  <EmptyMessage>
                    {eligibleTemplates.length === 0 ? 'No forms available.' : 'No forms match your search.'}
                  </EmptyMessage>
                ) : (
                  filteredTemplates.map(t => (
                    <Checkbox key={t.id}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(t.id)}
                        onChange={() => toggleTemplate(t.id)}
                        disabled={saving}
                      />
                      &nbsp;
                      {t.name}
                      {/* the space keeps "Scale" a word of its own in the checkbox's name */}
                      {hasScaleQuestion(t) && <>{' '}<ScaleTag>Scale</ScaleTag></>}
                    </Checkbox>
                  ))
                )}
              </TemplateList>
            </>
          )}
        </Content>
      </SideModal>
    </Form>
  );
}

function hasScaleQuestion(template) {
  return (template.questions || []).some(getScaleTarget);
}

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ScaleTag = styled.span`
  margin-left: 0.5rem;
  padding: 0 0.375rem;
  border-radius: 0.25rem;
  background: #eef2ff;
  color: #3b4cca;
  font-size: 0.75rem;
  font-weight: 500;
`;

const Content = styled(SideModal.Content)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 24rem;
`;

const Description = styled.p`
  margin: 0;
  color: #555;
  font-size: 0.875rem;
`;

const SelectionSummary = styled.div`
  font-size: 0.85rem;
  color: #747474;
`;

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  color: #747474;
  font-size: 0.9rem;
  font-style: italic;
`;

export default AttachTemplatesModal;